import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PLATFORM_LABELS, getAdAttribution } from '../utils/adAttribution';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Plus, Search, Eye, Edit, Trash2, Settings2, ChevronDown, ArrowUp, ArrowDown, ChevronsUpDown, Users, Download, Megaphone, X } from 'lucide-react';
import { useQuery } from 'react-query';
import api, { leadsAPI, organizationsAPI } from '../services/api';
import ConfirmationModal from '../components/ConfirmationModal';
import { useAuth } from '../contexts/AuthContext';
import useClickOutside from '../hooks/useClickOutside';
import useLeadClaim from '../hooks/useLeadClaim';
import { useOpenDentalBooking } from '../contexts/OpenDentalBookingContext';
import { LEAD_VIEWS } from './leadViews';
import { LEAD_COLUMNS_BY_KEY, customFieldColumns, headerClassFor, cellClassFor } from './leadColumns';
import { parseFiltersFromUrl, toApiQuery, filtersToUrlParams } from './leadListQuery';
import { formatDate } from '../utils/formatters';

// One list, several views (leadViews.js): Leads, Customers. The view is
// data; this component never branches on which view it is.
const LeadsList = ({ view = 'leads' }) => {
  const cfg = LEAD_VIEWS[view];
  const { user: currentUser, organization } = useAuth();
  // Words for this view ("lead" / "customer").
  const cap = (w) => w.charAt(0).toUpperCase() + w.slice(1);
  const nounFor = (n) => (n === 1 ? cfg.noun.one : cfg.noun.many);
  // A date filter value as a day: a bare YYYY-MM-DD is that calendar day
  // (new Date would read it as UTC midnight, the day before in the US).
  const dayLabel = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
    return formatDate(m ? new Date(+m[1], +m[2] - 1, +m[3]) : v);
  };
  const { interceptWonTransition } = useOpenDentalBooking();
  // Per-row tel: click → claim guard. Hook init without `lead`; we
  // pass the per-row lead via requestClaimFor in the anchor onClick.
  const claim = useLeadClaim({ currentUser, noun: cfg.noun.one });
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: orgUsersResp } = useQuery(
    ['organization', 'users'],
    () => organizationsAPI.getUsers(),
    { staleTime: 60_000 }
  );
  const orgUsers = orgUsersResp?.data?.users || [];
  const userById = React.useMemo(() => {
    const m = new Map();
    for (const u of orgUsers) m.set(String(u._id), u);
    return m;
  }, [orgUsers]);

  // Org's pipeline stages — render the editable stage chip on each row and
  // populate the stage filter dropdown.
  const { data: stagesData } = useQuery(
    ['pipeline-stages'],
    async () => {
      const pipelines = await api.get('/pipelines').then((r) => r.data);
      if (!pipelines?.[0]) return [];
      return api.get(`/pipelines/${pipelines[0]._id}/stages`).then((r) => r.data);
    },
    { staleTime: 60_000 }
  );
  const stages = stagesData || [];
  const stageFilterOptions = stages.filter(cfg.stageOptions);

  // Lead-entity custom fields drive the bottom row of filters.
  const { data: leadCustomFields = [] } = useQuery(
    ['custom-fields-for-filter', 'lead'],
    () => api.get('/custom-fields?entity=lead').then((r) => r.data || []),
    { staleTime: 5 * 60_000 }
  );

  const [filters, setFilters] = useState(() => parseFiltersFromUrl(searchParams, cfg));

  // Arama için ayrı state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isBulkActionLoading, setIsBulkActionLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [assignDropdownOpen, setAssignDropdownOpen] = useState(false);
  // Pending assignment awaits operator confirmation:
  //   { userId: string | null, userLabel: string }
  // userId === null means "unassign" (clear assignedTo).
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const assignDropdownRef = useClickOutside(assignDropdownOpen, () => setAssignDropdownOpen(false));

  // Modal State
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    leadIds: [],
    isBulk: false
  });

  // Input referansı
  const searchInputRef = useRef(null);

  // Column visibility state, saved per view. Saved choices are laid over
  // the view's defaults, so a column added to the defaults later still
  // shows up for people who saved their choice before.
  const [visibleColumns, setVisibleColumns] = useState(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(cfg.storageKey) || 'null'); } catch (_) { saved = null; }
    if (!saved || typeof saved !== 'object') return cfg.columns.defaults;
    const known = (key) => cfg.columns.available.includes(key) || key.startsWith('cf_');
    return { ...cfg.columns.defaults, ...Object.fromEntries(Object.entries(saved).filter(([k]) => known(k))) };
  });

  const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);
  const columnDropdownRef = useRef(null);

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem(cfg.storageKey, JSON.stringify(visibleColumns));
  }, [visibleColumns, cfg.storageKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setColumnDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  // Picker entries: the view's columns, then the org's custom fields.
  const pickerColumns = cfg.columns.available.map((key) => LEAD_COLUMNS_BY_KEY[key]);
  const cfColumns = React.useMemo(() => customFieldColumns(leadCustomFields), [leadCustomFields]);

  // Merkezi veri çekme fonksiyonu
  const fetchLeads = useCallback(async (currentFilters, isBackground = false) => {
    try {
      if (!isBackground) {
        setIsLoading(true);
      }
      const response = await leadsAPI.getLeads(toApiQuery(currentFilters, cfg));
      setData(response.data);
      // Keep only selections still on screen, so bulk actions never reach
      // rows the user can no longer see.
      const onScreen = new Set((response.data?.leads || []).map((l) => l._id));
      setSelectedLeads((prev) => {
        const next = prev.filter((id) => onScreen.has(id));
        return next.length === prev.length ? prev : next;
      });
      setError(null);
    } catch (err) {
      console.error('API Error:', err);
      // Arka planda hata olursa kullanıcıyı rahatsız etme, sadece logla
      if (!isBackground) {
        setError(err);
      }
    } finally {
      if (!isBackground) {
        setIsLoading(false);
      }
    }
  }, [cfg]);

  // Lead'leri Excel olarak dışa aktar — ekrandaki filtreleri (arama,
  // statü, kaynak, öncelik, stage, custom field) aynen uygular ama
  // sayfalamayı yok sayar; eşleşen TÜM lead'ler indirilir.
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    const toastId = toast.loading('Preparing export...');
    try {
      const response = await leadsAPI.exportLeads(toApiQuery(filters, cfg, { forExport: true }));

      // Sunucudan dönen dosya adını yakala, yoksa tarihli bir ad üret
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${cfg.noun.many}-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${cap(cfg.noun.many)} exported`, { id: toastId });
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Export failed', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  }, [filters, cfg]);

  // URL parametrelerini sync et
  useEffect(() => {
    const newFilters = parseFiltersFromUrl(searchParams, cfg);

    // State'i güncelle ama fetch yapma, alttaki effect yapacak
    setFilters(prev => {
      // Sadece gerçekten değiştiyse güncelle
      if (JSON.stringify(prev) !== JSON.stringify(newFilters)) {
        return { ...prev, ...newFilters };
      }
      return prev;
    });
    setSearchTerm(newFilters.search);
  }, [searchParams, cfg]);

  // Filters değiştiğinde API çağrısı yap
  useEffect(() => {
    fetchLeads(filters, false);
  }, [filters, fetchLeads]);

  // A page past the end (its last rows were moved, deleted or reassigned,
  // or the URL asked for it): step back to the last page.
  useEffect(() => {
    const p = data?.pagination;
    if (!p || data.leads?.length || !(p.totalLeads > 0)) return;
    if (p.currentPage !== filters.page || p.currentPage <= p.totalPages) return;
    const next = { ...filters, page: Math.max(1, p.totalPages) };
    setFilters(next);
    if (searchParams.has('page')) setSearchParams(filtersToUrlParams(next), { replace: true });
  }, [data, filters, searchParams, setSearchParams]);

  // Arama değişikliği - sadece local state'i güncelle
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Enter tuşuna basıldığında arama yap
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSelectedLeads([]);
      const newFilters = {
        ...filters,
        search: searchTerm,
        page: 1
      };
      setFilters(newFilters);
      setSearchParams(filtersToUrlParams(newFilters));
    }
  };

  // Arama butonuna tıklandığında
  const handleSearchClick = () => {
    setSelectedLeads([]);
    const newFilters = {
      ...filters,
      search: searchTerm,
      page: 1
    };
    setFilters(newFilters);
    setSearchParams(filtersToUrlParams(newFilters));
  };

  // SSE bağlantısı - real-time güncellemeler için
  useEffect(() => {
    const _token = localStorage.getItem('token');
    if (!_token) return;
    const eventSource = new EventSource(`${process.env.REACT_APP_API_URL || 'http://localhost:5002/api'}/events?token=${encodeURIComponent(_token)}`);

    eventSource.onmessage = (event) => {
      try {
        const eventData = JSON.parse(event.data);

        // Lead ile ilgili tüm event'lerde listeyi güncelle (BACKGROUND MODE)
        if (['lead_created', 'lead_updated', 'lead_deleted', 'lead_status_updated', 'lead_priority_updated', 'lead_assigned', 'lead_stage_updated'].includes(eventData.type)) {
          fetchLeads(filters, true); // true = background update, no spinner
        }
      } catch (error) {
        console.error('SSE parsing error:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE Error:', error);
    };

    return () => {
      eventSource.close();
    };
  }, [filters, fetchLeads]);

  const handleFilterChange = (key, value) => {
    setSelectedLeads([]);
    const newFilters = {
      ...filters,
      [key]: value,
      page: 1
    };
    setFilters(newFilters);
    setSearchParams(filtersToUrlParams(newFilters));
  };

  const handleCustomFieldChange = (cfKey, value) => {
    const newCustomFields = { ...(filters.customFields || {}) };
    if (value === '' || value === undefined || value === null) {
      delete newCustomFields[cfKey];
    } else {
      newCustomFields[cfKey] = value;
    }
    const newFilters = { ...filters, customFields: newCustomFields, page: 1 };
    setFilters(newFilters);
    setSearchParams(filtersToUrlParams(newFilters));
  };

  const handlePageChange = (page) => {
    setSelectedLeads([]);
    setFilters(prev => ({ ...prev, page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (sortBy) => {
    setSelectedLeads([]);
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: prev.sortBy === sortBy && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Renders the sort indicator for a column header. Three states: this is
  // the active sort (Up/Down based on order), or it's sortable but inactive
  // (faded ↕ icon).
  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column) {
      return <ChevronsUpDown size={12} className="text-gray-300" />;
    }
    return filters.sortOrder === 'asc'
      ? <ArrowUp size={12} className="text-primary-600" />
      : <ArrowDown size={12} className="text-primary-600" />;
  };

  // Toplu işlem fonksiyonları
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeads(data?.leads?.map(lead => lead._id) || []);
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    if (checked) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  // Delete handlers
  const openBulkDeleteModal = () => {
    if (selectedLeads.length === 0) return;
    setDeleteModal({
      isOpen: true,
      leadIds: selectedLeads,
      isBulk: true
    });
  };

  // The selection can shrink while a confirm dialog is open (a refresh
  // drops rows that left the list): the dialogs follow it, and close when
  // nothing is left to act on.
  useEffect(() => {
    if (selectedLeads.length > 0) return;
    setDeleteModal((m) => (m.isOpen ? { isOpen: false, leadIds: [], isBulk: false } : m));
    setPendingAssignment(null);
  }, [selectedLeads]);

  const handleConfirmDelete = async () => {
    const leadIds = deleteModal.leadIds.filter((id) => selectedLeads.includes(id));
    if (leadIds.length === 0) return;
    try {
      setIsBulkActionLoading(true);

      await leadsAPI.bulkAction({
        action: 'delete',
        leadIds
      });

      // Listeyi yenile ve seçimleri temizle
      setSelectedLeads([]);
      await fetchLeads(filters, true); // Background fetch

      toast.success(`${leadIds.length} ${nounFor(leadIds.length)} deleted successfully!`);
      setDeleteModal({ isOpen: false, leadIds: [], isBulk: false });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('An error occurred during deletion.');
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  // Bulk assignment — apply a single owner (or unassign) to every
  // selected lead in one request. The server's /bulk endpoint
  // already supports `assignTo`; we just fan the choice in here.
  const handleConfirmAssignment = async () => {
    if (!pendingAssignment || selectedLeads.length === 0) return;
    try {
      setIsBulkActionLoading(true);
      await leadsAPI.bulkAction({
        action: 'assignTo',
        leadIds: selectedLeads,
        data: { assignedTo: pendingAssignment.userId },
      });
      const count = selectedLeads.length;
      setSelectedLeads([]);
      await fetchLeads(filters, true);
      toast.success(
        pendingAssignment.userId
          ? `${count} ${nounFor(count)} assigned to ${pendingAssignment.userLabel}`
          : `${count} ${nounFor(count)} unassigned`
      );
      setPendingAssignment(null);
    } catch (error) {
      console.error('Bulk assign error:', error);
      toast.error(`An error occurred while assigning ${cfg.noun.many}.`);
    } finally {
      setIsBulkActionLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadsAPI.updateLead(leadId, { status: newStatus });
      toast.success('Status updated successfully!');
      fetchLeads(filters, true); // Refresh list in background
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };

  const handleStageChange = async (lead, newStageId) => {
    try {
      // OpenDental: moving into a Won stage from the list books an
      // appointment first (same interception as the board / lead detail).
      const targetStage = (stages || []).find((s) => String(s._id) === String(newStageId));
      if (targetStage?.closedType === 'won') {
        const decision = await interceptWonTransition({ lead, targetStage });
        if (decision === 'cancelled') return;
      }
      await leadsAPI.updateLead(lead._id, { stageId: newStageId });
      toast.success('Stage updated');
      fetchLeads(filters, true);
    } catch (error) {
      console.error('Stage update error:', error);
      toast.error('Failed to update stage');
    }
  };

  const handlePriorityChange = async (leadId, newPriority) => {
    try {
      await leadsAPI.updateLead(leadId, { priority: newPriority });
      toast.success('Priority updated successfully!');
      fetchLeads(filters, true); // Refresh list in background
    } catch (error) {
      console.error('Priority update error:', error);
      toast.error('Failed to update priority');
    }
  };

  // Any filter narrowing the list (decides which empty state to show).
  const hasActiveFilters = Boolean(
    filters.search || filters.status || filters.stageId || filters.source || filters.priority
    || filters.adId || filters.adsetId || filters.campaignId || filters.adPlatform
    || (cfg.extraFilters || []).some((k) => filters[k])
    || Object.values(filters.customFields || {}).some(Boolean)
  );
  const clearFilters = () => {
    // Narrowing filters only: per page and sort stay as they are.
    const next = {
      ...parseFiltersFromUrl(new URLSearchParams(), cfg),
      limit: filters.limit,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    };
    setSearchTerm('');
    setFilters(next);
    setSearchParams(filtersToUrlParams(next));
  };

  // Columns on screen: the view's columns switched on (and allowed), then
  // the custom fields switched on.
  const cellCtx = {
    currentUser,
    organization,
    linkBase: cfg.linkBase,
    stages,
    userById,
    usersLoaded: !!orgUsersResp,
    claim,
    onStageChange: handleStageChange,
    onPriorityChange: handlePriorityChange,
    onClaimed: (lead) => setData((prev) => prev ? {
      ...prev,
      leads: (prev.leads || []).map((row) =>
        String(row._id) === String(lead._id)
          ? { ...row, assignedTo: String(currentUser?._id) }
          : row
      ),
    } : prev),
  };
  const deleteCount = deleteModal.leadIds.filter((id) => selectedLeads.includes(id)).length;
  const shownColumns = [
    ...pickerColumns.filter((col) => visibleColumns[col.key] && (!col.gate || col.gate(cellCtx))),
    ...cfColumns.filter((col) => visibleColumns[col.key]),
  ];

  return (
    <div className="w-full px-4 py-8">
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ ...deleteModal, isOpen: false })}
        onConfirm={handleConfirmDelete}
        title={`Delete ${deleteCount} ${cap(nounFor(deleteCount))}`}
        message={`Are you sure you want to delete ${deleteCount} ${nounFor(deleteCount)}? This action cannot be undone and will remove all associated data.`}
        confirmText={`Delete ${deleteCount} ${cap(nounFor(deleteCount))}`}
        isDanger={true}
        isLoading={isBulkActionLoading}
      />
      <ConfirmationModal
        isOpen={!!pendingAssignment}
        onClose={() => setPendingAssignment(null)}
        onConfirm={handleConfirmAssignment}
        title={pendingAssignment?.userId
          ? `Assign ${selectedLeads.length} ${nounFor(selectedLeads.length)} to ${pendingAssignment.userLabel}?`
          : `Unassign ${selectedLeads.length} ${nounFor(selectedLeads.length)}?`}
        message={pendingAssignment?.userId
          ? `${pendingAssignment.userLabel} will become the owner of these ${cfg.noun.many}. Their previous owner (if any) will be replaced.`
          : `These ${cfg.noun.many} will be unassigned and become open for any team member to claim.`}
        confirmText={pendingAssignment?.userId ? 'Assign' : 'Unassign'}
        isLoading={isBulkActionLoading}
      />
      <ConfirmationModal {...claim.confirmProps} />

      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{cfg.title}</h1>
          <p className="mt-2 text-gray-600">
            Total {data?.pagination?.totalLeads || 0} {nounFor(data?.pagination?.totalLeads || 0)}
          </p>
          {(filters.wonFrom || filters.wonTo) && (
            <div className="mt-2 inline-flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-lg bg-green-50 border border-green-200 text-xs text-green-800">
              <span>
                {filters.wonFrom && filters.wonTo
                  ? `Won ${dayLabel(filters.wonFrom)} to ${dayLabel(filters.wonTo)}`
                  : filters.wonFrom ? `Won since ${dayLabel(filters.wonFrom)}` : `Won through ${dayLabel(filters.wonTo)}`}
              </span>
              <button
                type="button"
                aria-label="Clear won date filter"
                onClick={() => {
                  const next = { ...filters, wonFrom: '', wonTo: '', page: 1 };
                  setFilters(next);
                  setSearchParams(filtersToUrlParams(next));
                }}
                className="inline-flex items-center gap-0.5 text-green-700 hover:text-green-900 font-medium"
              >
                <X size={12} /> Clear
              </button>
            </div>
          )}
          {(filters.adId || filters.adsetId || filters.campaignId || filters.adPlatform) && (
            <div className="mt-2 inline-flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
              <Megaphone size={12} />
              <span>
                Showing {cfg.noun.many} from{' '}
                {(() => {
                  // Every lead in a filtered result shares the ad / ad set /
                  // campaign, so borrow the human name from the first row.
                  const attr = getAdAttribution(data?.leads?.[0]);
                  const named = (node, kind, id) => node?.name
                    ? <>{kind} <span className="font-semibold">{node.name}</span></>
                    : <>{kind} <span className="font-mono">{id}</span></>;
                  if (filters.adId) return named(attr?.ad, 'ad', filters.adId);
                  if (filters.adsetId) return named(attr?.adset, 'ad set', filters.adsetId);
                  if (filters.campaignId) return named(attr?.campaign, 'campaign', filters.campaignId);
                  return PLATFORM_LABELS[filters.adPlatform] || filters.adPlatform;
                })()}
              </span>
              <button
                type="button"
                aria-label="Clear ad filter"
                onClick={() => {
                  const next = { ...filters, adId: '', adsetId: '', campaignId: '', adPlatform: '', page: 1 };
                  setFilters(next);
                  setSearchParams(filtersToUrlParams(next));
                }}
                className="inline-flex items-center gap-0.5 text-blue-700 hover:text-blue-900 font-medium"
              >
                <X size={12} /> Clear
              </button>
              <Link to="/reports/ads" className="text-blue-700 hover:underline">Report</Link>
            </div>
          )}
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center gap-3">
          {selectedLeads.length > 0 && (currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
            <div className="relative" ref={assignDropdownRef}>
              <button
                onClick={() => setAssignDropdownOpen((v) => !v)}
                disabled={isBulkActionLoading}
                className="btn-secondary flex items-center"
              >
                <Users size={16} className="mr-2" />
                Assign {selectedLeads.length} {cap(nounFor(selectedLeads.length))}
                <ChevronDown size={16} className={`ml-1 transition-transform ${assignDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {assignDropdownOpen && (
                <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg z-20">
                  <button
                    type="button"
                    onClick={() => {
                      setAssignDropdownOpen(false);
                      setPendingAssignment({ userId: null, userLabel: 'Unassigned' });
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100"
                  >
                    🔓 Unassign (clear owner)
                  </button>
                  {orgUsers.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-400">No team members</div>
                  ) : (
                    orgUsers.map((u) => (
                      <button
                        key={u._id}
                        type="button"
                        onClick={() => {
                          setAssignDropdownOpen(false);
                          setPendingAssignment({ userId: String(u._id), userLabel: u.name || u.email });
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {u.name || u.email}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          {selectedLeads.length > 0 && currentUser?.permissions?.delete_lead && (
            <button
              onClick={openBulkDeleteModal}
              disabled={isBulkActionLoading}
              className="btn-danger flex items-center"
            >
              <Trash2 size={16} className="mr-2" />
              {isBulkActionLoading ? 'Selected...' : `Delete ${selectedLeads.length} ${cap(nounFor(selectedLeads.length))}`}
            </button>
          )}

          {/* Column Selector */}
          <div className="relative" ref={columnDropdownRef}>
            <button
              onClick={() => setColumnDropdownOpen(!columnDropdownOpen)}
              className="btn-secondary flex items-center"
            >
              <Settings2 size={16} className="mr-2" />
              Columns
              <ChevronDown size={16} className={`ml-1 transition-transform ${columnDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {columnDropdownOpen && (
              <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-h-[70vh] overflow-y-auto">
                <div className="px-3 py-2 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Show Columns</span>
                </div>
                {pickerColumns.filter((col) => !col.gate || col.gate(cellCtx)).map(({ key, label }) => (
                  <label
                    key={key}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={!!visibleColumns[key]}
                      onChange={() => toggleColumn(key)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
                {leadCustomFields.length > 0 && (
                  <>
                    <div className="px-3 py-2 mt-1 border-t border-gray-100">
                      <span className="text-xs font-semibold text-gray-500 uppercase">Custom Fields</span>
                    </div>
                    {leadCustomFields.map((cf) => {
                      const colKey = `cf_${cf.key}`;
                      return (
                        <label
                          key={colKey}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={!!visibleColumns[colKey]}
                            onChange={() => toggleColumn(colKey)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                          />
                          <span className="text-sm text-gray-700">{cf.label}</span>
                        </label>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="btn-secondary flex items-center"
              title={`Export ${cfg.noun.many} to Excel (applies current filters)`}
            >
              <Download size={16} className="mr-2" />
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          )}

          {cfg.createLink && (
            <Link
              to={cfg.createLink}
              className="btn-primary"
            >
              <Plus size={16} className="mr-2" />
              New Lead
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Name, email, company..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className="input pl-10 pr-10"
                />
                <button
                  onClick={handleSearchClick}
                  aria-label="Search"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Search size={16} />
                </button>
              </div>
            </div>

            {/* Status filter: the view's stages (Leads: open and Lost;
                Customers: Won stages). Hidden when there is nothing to
                choose between, unless one is already chosen. */}
            {(stageFilterOptions.length > 1 || filters.stageId) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.stageId || ''}
                onChange={(e) => handleFilterChange('stageId', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                {stageFilterOptions.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
            )}

            {/* Source Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <select
                value={filters.source}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="website">Website</option>
                <option value="phone">Phone</option>
                <option value="phone_agent">Phone Agent</option>
                <option value="email">Email</option>
                <option value="social_media">Social Media</option>
                <option value="business_card">Business Card</option>
                <option value="referral">Referral</option>
                <option value="google_ads">Google Ads</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="input"
              >
                <option value="">All</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {/* Limit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Per Page
              </label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="input"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

        </div>
      </div>

      {/* Table */}
      <div className="card min-h-[400px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="loading-spinner"></div>
            <span className="ml-2 text-gray-600">Loading {cfg.noun.many}...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 mb-4">
              <Search size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Occurred</h3>
            <p className="text-gray-600">An error occurred while loading the {cfg.noun.many} list.</p>
          </div>
        ) : (
          <div className="card-body p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={data?.leads?.length > 0 && selectedLeads.length === data.leads.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    {shownColumns.map((col) => (
                      <th key={col.key} className={headerClassFor(col, cfg.columns.hideBelow?.[col.key])}>
                        <button onClick={() => handleSort(col.sortKey)} className="flex items-center space-x-1 hover:text-gray-700">
                          {col.icon && <col.icon size={14} />}
                          <span>{col.label}</span>
                          <SortIcon column={col.sortKey} />
                        </button>
                      </th>
                    ))}
                    <th className="w-16 px-3 py-3 text-left text-xs font-medium text-gray-500 tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.leads?.map((lead) => (
                    <motion.tr
                      key={lead._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`${lead.status === 'do_not_call' ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50'}`}
                    >
                      <td className="w-12 px-3 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead._id)}
                          onChange={(e) => handleSelectLead(lead._id, e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      {shownColumns.map((col) => (
                        <td key={col.key} className={cellClassFor(col, cfg.columns.hideBelow?.[col.key])}>
                          {col.render(lead, cellCtx)}
                        </td>
                      ))}
                      <td className="w-16 px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/leads/${lead._id}`}
                            className="text-primary-600 hover:text-primary-700"
                            title="View"
                          >
                            <Eye size={16} />
                          </Link>
                          <Link
                            to={`/leads/${lead._id}/edit`}
                            className="text-gray-600 hover:text-gray-700"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </Link>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data?.leads?.length === 0 && !(data.pagination?.totalLeads > 0) && cfg.emptyState && (() => {
              const state = hasActiveFilters ? cfg.emptyState.zeroMatch : cfg.emptyState.zeroData;
              return (
                <div className="text-center py-12 px-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">{state.title}</h3>
                  {state.hint && <p className="text-gray-600">{state.hint}</p>}
                  {hasActiveFilters && (
                    <button type="button" onClick={clearFilters} className="btn-secondary mt-4">
                      Clear filters
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Pagination */}
        {data?.pagination && data.pagination.totalLeads > 0 && !isLoading && !error && (
          <div className="card-body border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-gray-700">
                <span>
                  {((data.pagination.currentPage - 1) * filters.limit) + 1} -{' '}
                  {Math.min(data.pagination.currentPage * filters.limit, data.pagination.totalLeads)} /{' '}
                  {data.pagination.totalLeads} results
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(data.pagination.currentPage - 1)}
                  disabled={!data.pagination.hasPrevPage}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {data.pagination.currentPage} / {data.pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(data.pagination.currentPage + 1)}
                  disabled={!data.pagination.hasNextPage}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsList;